import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Lock, ShieldCheck, PhoneCall, Smartphone, UserCog, FolderArchive, Scale } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1606022831434-91293aebf25a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxwb2xpY2UlMjBjYXIlMjBuaWdodHxlbnwwfHx8fDE3ODUzNjg2ODB8MA&ixlib=rb-4.1.0&q=85";

const PORTALS = [
  { key: "supervisor", label: "Supervisor", desc: "Reports · approvals · all calls", path: "/supervisor", icon: ShieldCheck, color: "#8B5CF6" },
  { key: "calltaker", label: "Call-Taker / Dispatcher", desc: "9-1-1 intake · dispatch", path: "/new-call", icon: PhoneCall, color: "#3B82F6" },
  { key: "mdt", label: "MDT — Field", desc: "Police · Fire · EMS terminal", path: "/mdt", icon: Smartphone, color: "#10B981" },
  { key: "records", label: "Records / RMS", desc: "Records · warrants · evidence", path: "/rms", icon: FolderArchive, color: "#0EA5E9", seal: "/brand/records-seal.png" },
  { key: "ia", label: "Internal Affairs", desc: "Complaints · investigations · findings", path: "/ia", icon: Scale, color: "#DC2626", seal: "/brand/ia-seal.png" },
  { key: "admin", label: "Administrator", desc: "Provision & manage accounts", path: "/admin", icon: UserCog, color: "#F59E0B" },
];

const ROLE_PORTALS = {
  pa_state_constable: ["mdt"],
  field_unit: ["mdt"],
  call_taker: ["calltaker"],
  police_dispatcher: ["calltaker"],
  fire_dispatcher: ["calltaker"],
  ems_dispatcher: ["calltaker"],
  supervisor: ["supervisor", "calltaker", "records", "ia"],
  watch_commander: ["supervisor", "calltaker", "records", "ia"],
  records_supervisor: ["records", "supervisor", "ia"],
};
const DEFAULT_LANDING = {
  pa_state_constable: "/mdt", field_unit: "/mdt",
  call_taker: "/new-call", police_dispatcher: "/dispatch", fire_dispatcher: "/dispatch", ems_dispatcher: "/dispatch",
  supervisor: "/supervisor", watch_commander: "/supervisor", records_supervisor: "/rms", admin: "/admin",
};

const inputCls = "mt-1 w-full bg-[#141518] border border-[#2C2D35] px-3 py-2.5 text-sm outline-none focus:border-[#B98A1E] font-mono rounded-sm transition-colors";

export default function Login() {
  const { login, pinLogin } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [pinBusy, setPinBusy] = useState(false);
  const [portal, setPortal] = useState(null);
  const [ack, setAck] = useState(false);
  const ACK_MSG = "Check the box to acknowledge the system-use and monitoring notice before signing in.";

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("expired")) {
      toast.warning("Signed out after 30 minutes of inactivity (security policy). Please sign in again.",
        { id: "session-expired", duration: 8000 });
      window.history.replaceState({}, "", "/login");
    }
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (!ack) { setErr(ACK_MSG); return; }
    setErr(""); setBusy(true);
    toast.dismiss("session-expired");
    try {
      const u = await login(email, password, true);
      const sel = PORTALS.find((p) => p.key === portal);
      if (sel && u.role !== "admin" && !(ROLE_PORTALS[u.role] || []).includes(sel.key)) {
        const roleName = (u.role || "").replace(/_/g, " ");
        toast.error(`Your account (${roleName}) is not authorized for the ${sel.label} terminal.`);
        nav(DEFAULT_LANDING[u.role] || "/mdt");
        return;
      }
      nav(sel?.path || DEFAULT_LANDING[u.role] || "/");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const pinSubmit = async () => {
    if (!ack) { setErr(ACK_MSG); return; }
    setErr(""); setPinBusy(true);
    // Admin quick PIN honors the selected terminal — e.g. jump straight into
    // Supervisor, MDT or Internal Affairs. PIN accounts are admin (bypass all gates).
    try {
      await pinLogin(pin, true);
      const sel = PORTALS.find((p) => p.key === portal);
      nav(sel?.path || "/admin");
    }
    catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message); }
    finally { setPinBusy(false); }
  };

  return (
    <div className="app-viewport w-screen flex flex-col bg-[#0A0A0A] text-[#F9FAFB] overflow-y-auto">
      {/* Classification / official banner */}
      <div data-testid="login-classification-banner" className="shrink-0 w-full bg-[#7F1D1D] text-white text-center font-mono text-[10px] tracking-[0.3em] py-1 uppercase">
        Official Use Only — Law Enforcement Sensitive — Criminal Justice Information System
      </div>
      {/* Government header bar */}
      <div className="shrink-0 w-full bg-[#0E1B2E] border-b-2 border-[#B98A1E]">
        <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/agency-logo.jpg" alt="Matcom Office Of Public Safety" className="w-10 h-10 object-contain rounded-full border border-[#B98A1E]/60 bg-white/5" />
            <div className="min-w-0">
              <div className="font-display font-black tracking-tight text-base leading-none uppercase whitespace-nowrap">Matcom Office of Public Safety</div>
              <div className="font-mono text-[10px] tracking-[0.2em] text-[#B98A1E] uppercase mt-1 whitespace-nowrap truncate">
                Integrated CAD · MDT · RMS · NG9-1-1<span className="hidden xl:inline"> — Secure Access Portal</span>
              </div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-4 lg:gap-5 font-mono text-[10px] tracking-[0.15em] lg:tracking-[0.2em] text-[#9CA3AF] uppercase whitespace-nowrap shrink-0">
            <span className="flex items-center gap-1.5"><span className="live-dot w-2 h-2 rounded-full bg-[#22C55E] after:bg-[#22C55E]" /> System Online</span>
            <span className="hidden lg:inline">CJIS Security Policy v5.9</span>
            <span className="hidden lg:inline">FIPS 140-2 TLS</span>
            <span className="flex items-center gap-2 pl-3 border-l border-[#B98A1E]/40">
              <img src="/brand/records-seal.png" alt="Records Division seal" className="w-8 h-8 rounded-full" />
              <img src="/brand/ia-seal.png" alt="Internal Affairs seal" className="w-8 h-8 rounded-full" />
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex min-h-0">
      {/* Hero */}
      <div className="hidden lg:block relative w-1/2 tac-grain">
        <img src={HERO} alt="patrol" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/80 to-[#0A0A0A]/30" />
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="absolute top-8 left-10 z-10 font-mono text-[10px] tracking-[0.3em] text-[#B98A1E] uppercase leading-relaxed border-l-2 border-[#B98A1E] pl-3">
          Records Division<br />Internal Affairs<br />Communications
        </div>
        <div className="absolute bottom-12 left-10 right-10 anim-in">
          <div className="tac-label mb-3 text-[#B98A1E]">Public Safety Communications & Records</div>
          <div className="font-display font-black text-5xl tracking-tighter uppercase leading-[0.95]">Unified<br />Public-Safety<br />Operations</div>
          <p className="font-mono text-xs text-[#9CA3AF] mt-5 max-w-md leading-relaxed">
            CAD · MDT · RMS · NG911 · GIS — a single command environment for Police, Fire, and EMS across
            municipal, county, state, tribal and federal agencies. Every action audited.
          </p>
          <div data-testid="login-system-notice" className="mt-6 max-w-md border border-[#B98A1E]/40 bg-[#0E1B2E]/80 p-3 font-mono text-[10px] text-[#C9CDD4] leading-relaxed">
            <b className="text-[#B98A1E]">U.S. GOVERNMENT / LAW ENFORCEMENT SYSTEM — NOTICE OF MONITORING.</b> This information system is for authorized
            criminal-justice use only. By accessing it you consent to monitoring, recording and audit of all activity.
            Unauthorized or improper use may result in administrative, civil and criminal penalties under 18 U.S.C. § 1030,
            state law and the FBI CJIS Security Policy. All transactions are retained for audit.
          </div>
        </div>
      </div>

      {/* Auth panel */}
      <div className="flex-1 flex items-start lg:items-center justify-center p-6 lg:p-8 grid-lines overflow-y-auto">
        <div className="w-full max-w-sm anim-in py-2">
          <div className="flex items-center gap-2.5 mb-6">
            <Lock size={14} className="text-[#B98A1E]" />
            <div>
              <div className="font-display font-black tracking-tight text-lg leading-none uppercase">Authorized Personnel Sign-In</div>
              <div className="tac-label mt-1.5">Secure Dispatch & Records Terminal</div>
            </div>
          </div>

          <div className="mb-5">
            <div className="tac-label mb-2">Select your terminal</div>
            <div className="grid grid-cols-2 gap-2">
              {PORTALS.map((p) => (
                <button key={p.key} type="button" data-testid={`portal-${p.key}`} onClick={() => setPortal(p.key)}
                  className="text-left p-2.5 border rounded-sm tac-hover tac-corners hover:-translate-y-0.5 relative"
                  style={portal === p.key
                    ? { borderColor: p.color, background: p.color + "1A", boxShadow: `0 0 0 1px ${p.color}55` }
                    : { borderColor: "#2C2D35", background: "#141518" }}>
                  {p.seal ? <img src={p.seal} alt="" className="absolute top-2 right-2 w-7 h-7 rounded-full opacity-90" /> : null}
                  <p.icon size={16} strokeWidth={1.75} style={{ color: p.color }} />
                  <div className="font-head font-semibold text-xs mt-1.5 leading-tight">{p.label}</div>
                  <div className="font-mono text-[9px] text-[#9CA3AF] mt-0.5 leading-tight">{p.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="tac-label">Operator ID / Agency Email</label>
              <input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)}
                className={inputCls} placeholder="username or email" autoComplete="username" />
            </div>
            <div>
              <label className="tac-label">Passphrase</label>
              <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className={inputCls} placeholder="••••••••" autoComplete="current-password" />
            </div>
            <label data-testid="login-ack-label" className={`flex items-start gap-2.5 border px-3 py-2.5 rounded-sm cursor-pointer transition-colors ${ack ? "border-[#B98A1E] bg-[#B98A1E]/10" : "border-[#2C2D35] bg-[#141518] hover:border-[#B98A1E]/60"}`}>
              <input data-testid="login-ack-checkbox" type="checkbox" checked={ack} onChange={(e) => { setAck(e.target.checked); if (e.target.checked) setErr(""); }}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[#B98A1E] cursor-pointer" />
              <span className="font-mono text-[10px] text-[#C9CDD4] leading-relaxed">
                <b className="text-[#F9FAFB]">I acknowledge the system-use notice.</b> I am authorized criminal-justice personnel, I consent to
                monitoring and audit of this session, and I understand unauthorized use is punishable under 18 U.S.C. § 1030 and the FBI CJIS Security Policy.
                <span className="block text-[#6B7280] mt-0.5">Required once per sign-in · recorded in the audit log.</span>
              </span>
            </label>
            {err && <div data-testid="login-error" className="text-xs text-[#EF4444] font-mono border border-[#EF4444]/40 bg-[#EF4444]/10 px-3 py-2 rounded-sm">{err}</div>}
            <button data-testid="login-submit" disabled={busy || !ack}
              className="w-full flex items-center justify-center gap-2 bg-[#0E1B2E] hover:bg-[#15284a] border border-[#B98A1E] text-white font-display font-bold uppercase tracking-wide py-2.5 text-sm rounded-sm tac-hover disabled:opacity-50 disabled:cursor-not-allowed">
              {busy && <Loader2 size={15} className="animate-spin" />} Sign In to Terminal
            </button>
          </form>

          <div className="mt-4 border-t border-[#2C2D35] pt-4">
            <div className="tac-label mb-2 flex items-center gap-1.5"><ShieldCheck size={12} /> Administrator Quick PIN</div>
            <div className="flex gap-2">
              <input data-testid="pin-input" type="password" inputMode="numeric" value={pin} maxLength={8}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} onKeyDown={(e) => e.key === "Enter" && pinSubmit()}
                className="flex-1 bg-[#141518] border border-[#2C2D35] px-3 py-2.5 text-sm outline-none focus:border-[#B98A1E] font-mono tracking-[0.4em] rounded-sm transition-colors"
                placeholder="• • • • •" autoComplete="off" />
              <button data-testid="pin-submit" onClick={pinSubmit} disabled={pinBusy || pin.length < 4 || !ack}
                className="px-4 bg-[#B98A1E] hover:bg-[#a0761a] text-black font-display font-bold uppercase text-sm rounded-sm tac-hover disabled:opacity-50 flex items-center gap-1.5">
                {pinBusy && <Loader2 size={14} className="animate-spin" />} PIN
              </button>
            </div>
          </div>

          <div className="mt-6 flex items-start gap-2 border border-[#2C2D35] bg-[#0d0e11] px-3 py-2.5 rounded-sm">
            <Lock size={13} className="text-[#B98A1E] shrink-0 mt-0.5" />
            <div className="font-mono text-[10px] text-[#9CA3AF] leading-relaxed">
              <b className="text-[#C9CDD4]">Authorized personnel only.</b> Access is restricted to credentialed criminal-justice personnel.
              All sessions are logged and audited under the FBI CJIS Security Policy and 28 CFR Part 20. Accounts are
              provisioned by your agency administrator — there is no public registration.
            </div>
          </div>
          <div className="lg:hidden mt-3 font-mono text-[9px] text-[#6B7280] leading-relaxed">
            U.S. Government / law-enforcement system. Use constitutes consent to monitoring. Unauthorized use is punishable under 18 U.S.C. § 1030.
          </div>
        </div>
      </div>
      </div>

      {/* Official footer */}
      <div data-testid="login-gov-footer" className="shrink-0 w-full bg-[#0E1B2E] border-t border-[#B98A1E]/40 px-6 py-2 flex flex-wrap items-center justify-between gap-2 font-mono text-[9px] tracking-[0.15em] text-[#9CA3AF] uppercase">
        <span>© {new Date().getFullYear()} Matcom Office of Public Safety · An official law-enforcement information system</span>
        <span className="flex gap-4"><span>Privacy Act Notice</span><span>Accessibility § 508</span><span>Records Division</span><span>Internal Affairs</span><span>System Status: Operational</span></span>
      </div>
    </div>
  );
}
